import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel"; // (לא חובה בפועל למסך הזה)
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
} from "@mui/material";

const API_URL = "https://xjgq-a7mn-52gg.n7e.xano.io/api:HZkjR1CW/questions";


export default function QuizPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
const [results, setResults] = useState([]);

  // null/"" => כל הנושאים/כל הרמות
  const topic = state?.topic ?? null;
  const difficulty = state?.difficulty ?? null;

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [i, setI] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);

  // שולחים ל-Xano רק פרמטרים שקיימים (בלי ריקים בכלל!)
  const params = useMemo(() => {
    const p = {};
    if (topic) p.topic = topic;
    // difficulty יכול להיות 0 במסכים מסוימים – בודקים truthy בצורה מפורשת
    if (difficulty !== null && difficulty !== "" && difficulty !== undefined) {
      p.difficulty = difficulty;
    }
    return p;
  }, [topic, difficulty]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_URL, { params });
        if (!mounted) return;
        setQuestions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error(e);
        if (mounted) setQuestions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [params]);

  // עוזר: שליפת שדות גם כשעטופים ב-data
  const getField = useCallback((q, key) => q?.data?.[key] ?? q?.[key], []);

  const current = questions[i];
  const questionText = getField(current, "text");
  const answersObj = getField(current, "answers") ?? {};
  const correctKey = getField(current, "correct");
  const explanation = getField(current, "explanation");

  // ערבוב תשובות – אבל יציב לפי מזהה השאלה כדי שלא ישתנה בכל רנדר
  const shuffledAnswers = useMemo(() => {
    const entries = Object.entries(answersObj);
    const seed = String(getField(current, "id") ?? i);
    // פונקציית shuffle יציבה פשוטה לפי seed
    const hash = [...seed].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 0);
    const arr = [...entries];
    for (let k = arr.length - 1; k > 0; k--) {
      const rnd = (hash + k * 9301) % (k + 1);
      [arr[k], arr[rnd]] = [arr[rnd], arr[k]];
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, i]);

  const total = questions.length;
  const progress = total ? Math.round(((i + (showExplanation ? 1 : 0)) / total) * 100) : 0;

  const handleAnswer = (key) => {
    if (showExplanation) return;
    setSelected(key);
    setShowExplanation(true);
    if (key === correctKey) setScore((s) => s + 1);
  };

 const next = () => {
  // מוסיפים את תוצאת השאלה הנוכחית
  if (current) {
    const questionId = getField(current, "id") ?? current?.id ?? i;
    const qText = getField(current, "text") ?? "";
    const answersObjLocal = getField(current, "answers") ?? {};
    setResults((prev) => [
      ...prev,
      {
        id: questionId,
        text: qText,
        answers: answersObjLocal,
        selectedKey: selected,
        correctKey,
        correct: selected === correctKey,
        explanation: getField(current, "explanation") ?? "",
      },
    ]);
  }

  // איפוס מצב בחירה והמשך
  setSelected(null);
  setShowExplanation(false);

  if (i + 1 < questions.length) {
    setI(i + 1);
  } else {
    // בסיום: עוברים למסך הסיכום עם כל הנתונים
    navigate("/results", {
      state: {
        score,
        total: questions.length,
        results,
      },
    });
  }
};

  // קיצורי מקלדת: 1..9 לבחירה
  useEffect(() => {
    const onKey = (e) => {
      if (showExplanation) return;
      const num = parseInt(e.key, 10);
      if (!Number.isNaN(num) && num >= 1 && num <= shuffledAnswers.length) {
        const [key] = shuffledAnswers[num - 1];
        handleAnswer(key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showExplanation, shuffledAnswers]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box
        sx={{
          direction: "rtl",
          minHeight: "75dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!questions.length) {
    return (
      <Box
        sx={{
          direction: "rtl",
          minHeight: "75dvh",
          backgroundImage: "url('/bg-badook.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Paper sx={{ p: 3, maxWidth: 600, width: "100%", textAlign: "right" }}>
          <Typography variant="h6" gutterBottom>
            לא נמצאו שאלות
          </Typography>
          <Typography variant="body2" gutterBottom>
            נסה לבחור נושא או רמת קושי אחרת.
          </Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate("/")}>
            חזרה למסך הבית
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        direction: "rtl",
        minHeight: "75dvh",
        backgroundImage: "url('/bg-badook.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 3,
          maxWidth: 700,
          width: "100%",
          textAlign: "left",
          bgcolor: "white",
          borderRadius: 3,
        }}
      >
        {/* כותרת + פס התקדמות + ניקוד */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" sx={{ opacity: 0.8 }}>
            שאלה {i + 1} מתוך {total} • ניקוד: {score}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mt: 1, height: 8, borderRadius: 999 }}
          />
        </Box>

        
         <Typography variant="h6" direction="rtl" textAlign="left" gutterBottom sx={{ mt: 1, whiteSpace: 'pre-wrap',  direction: 'rtl', wordBreak: 'normal' }}>
          
         {current?.data?.text ??  current?.text}?{"\u200F"}
        </Typography>

        {shuffledAnswers.map(([key, val], index) => {
          const isCorrect = key === correctKey;
          const isSelected = key === selected;

          let bg = "transparent";
          let color = "inherit";
          let borderColor = "rgba(0,0,0,0.23)";

          if (showExplanation) {
            if (isCorrect) {
              bg = "#4CAF50";
              color = "#fff";
              borderColor = "#4CAF50";
            } else if (isSelected) {
              bg = "#F44336";
              color = "#fff";
              borderColor = "#F44336";
            }
          }

          return (
            <Button
              key={key}
              fullWidth
              variant="outlined"
              sx={{
                my: 1,
                justifyContent: "flex-end",
                textAlign: "right",
                bgcolor: bg,
                color,
                borderColor,
                "&:hover": { bgcolor: bg, borderColor },
              }}
              onClick={() => handleAnswer(key)}
              disabled={showExplanation}
            >
              {/* מיספור תשובות */}
              {/* <Box component="span" sx={{ ml: 1, opacity: 0.7 }}>
                {index + 1}.
              </Box> */}
              {val}
            </Button>
          );
        })}

        {showExplanation && explanation && (
          <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
            {explanation}
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <Button onClick={() => navigate("/")} color="inherit">
            יציאה
          </Button>
          <Button variant="contained" onClick={next}>
            {i + 1 < questions.length ? "לשאלה הבאה" : "סיום"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
